import type React from "react";
import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { selectCurrentLocationStatus } from "../location/locationSlice";

import i18n from "@/app/i18n";
import { LocationStatusTypes } from "../utils/types";
import {
  InfoCircleIcon,
  OutlineLoadingIcon,
  WarningIcon,
} from "./components/icons";

import "./notification-handler.scss";

const DEFAULT_NOTIFICATION_DURATION = 3000;

enum NotificationType {
  INFO = "info",
  WARN = "warn",
  LOADING = "load",
}

interface Notification {
  message: string;
  type: NotificationType;
  duration?: number;
  willExit?: boolean;
  willEnter?: boolean;
}

const NotificationIcon: React.FC<{ type: NotificationType }> = ({ type }) => {
  const className = `notification-handler__icon-frame__icon ${type}`;
  switch (type) {
    case NotificationType.INFO:
      return <InfoCircleIcon className={className} />;
    case NotificationType.WARN:
      return <WarningIcon className={className} />;
    case NotificationType.LOADING:
      return <OutlineLoadingIcon className={className} />;
    default:
      return <></>;
  }
};

const NotificationContent: React.FC<
  Notification & {
    id: string;
    dismissNotification: (id: string) => void;
    onEnterAnimationFinished: (id: string) => void;
    onExitAnimationFinished: (id: string) => void;
  }
> = ({
  id,
  message,
  type,
  duration,
  willExit,
  willEnter,
  dismissNotification,
  onEnterAnimationFinished,
  onExitAnimationFinished,
}) => {
  useEffect(() => {
    if (!duration) return;

    const timeoutId = setTimeout(() => {
      dismissNotification(id);
    }, duration);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    const { animationName } = event;

    if (animationName === "slideIn") {
      onEnterAnimationFinished(id);
    } else if (animationName === "slideOut") {
      onExitAnimationFinished(id);
    }
  };

  return (
    <div
      key={id}
      id={id}
      className={`notification-handler__frame ${willExit ? "exit" : ""} ${
        willEnter ? "enter" : ""
      } ${type}`}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className={`notification-handler__icon-frame ${type}`}>
        <NotificationIcon type={type} />
      </div>
      <p className="notification-handler__notification-message">{message}</p>
    </div>
  );
};

const NotificationHandler: React.FC = () => {
  const [notifications, setNotifications] = useState<
    Record<string, Notification>
  >({});
  const currentLocationStatus = useSelector(selectCurrentLocationStatus);

  /**
   * Once notification finished animating the exit, remove
   * from state.
   *
   */
  const onExitAnimationFinished = (id: string) => {
    setNotifications((prevNotifications) => {
      const updatedNotifications = { ...prevNotifications };
      delete updatedNotifications[id];
      return updatedNotifications;
    });
  };

  /**
   * Lets notification know it is going to be removed.
   */
  const dismissNotification = (id: string) => {
    setNotifications((prevNotifications) => {
      const updatedNotifications = { ...prevNotifications };
      if (id in updatedNotifications) {
        updatedNotifications[id].willExit = true;
      }
      return updatedNotifications;
    });
  };

  /**
   * Called when notification's entering animation finishes
   * to update the notifications state.
   */
  const onEnterAnimationFinished = (id: string) => {
    setNotifications((prevNotifications) => {
      const updatedNotifications = { ...prevNotifications };
      if (id in updatedNotifications) {
        updatedNotifications[id].willEnter = false;
      }
      return updatedNotifications;
    });
  };

  /**
   * Creates a new notification and adds it
   * to the state.
   */
  const addNotification = (
    id: string,
    message: string,
    type: string,
    duration?: number,
    willExit = false,
    willEnter = true,
  ) => {
    const notification = {
      duration: duration || DEFAULT_NOTIFICATION_DURATION,
      message,
      type,
      willEnter,
      willExit,
    } as Notification;
    // For now we only allow one notification at a time
    setNotifications((prevNotifications) => {
      // Only update notifications if notification is not already being shown
      if (!(id in prevNotifications)) {
        return { [id]: notification };
      } else {
        return prevNotifications;
      }
    });
  };

  // Location
  useEffect(() => {
    if (currentLocationStatus === LocationStatusTypes.OOB) {
      addNotification(
        LocationStatusTypes.OOB,
        i18n.t("location.out_of_building"),
        NotificationType.WARN,
      );
    } else {
      dismissNotification(LocationStatusTypes.OOB);
    }
  }, [currentLocationStatus]);

  return Object.entries(notifications).length == 0 ? (
    <></>
  ) : (
    <div className="notification-handler">
      {Object.entries(notifications).map(([id, notification]) => (
        <NotificationContent
          key={id}
          id={id}
          message={notification.message}
          type={notification.type}
          duration={notification.duration}
          willExit={notification.willExit}
          willEnter={notification.willEnter}
          dismissNotification={dismissNotification}
          onEnterAnimationFinished={onEnterAnimationFinished}
          onExitAnimationFinished={onExitAnimationFinished}
        />
      ))}
    </div>
  );
};

export default NotificationHandler;
